// src/context/ImpersonationContext.jsx
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useGraphQL } from '../graphql';
import { useAuth } from '../components/AuthWrapper';

const ImpersonationContext = createContext();

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider = ({ children }) => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const { refreshAuth } = useAuth();
    const [impersonationInfo, setImpersonationInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [featureAvailable, setFeatureAvailable] = useState(true);
    const hasChecked = useRef(false);

    const checkImpersonationStatus = async () => {
        // Don't check if feature is known to be unavailable
        if (!featureAvailable) return;

        try {
            setLoading(true);
            const data = await executeQuery(queries.getImpersonationInfo());
            setImpersonationInfo(data?.impersonationInfo || null);
            setError(null);
        } catch (err) {
            console.error('Failed to check impersonation status:', err);
            // Check if this is a "field not found" error - means backend doesn't support it yet
            if (err.message?.includes('Cannot query field') || err.message?.includes('impersonationInfo')) {
                console.warn('Impersonation feature not available on backend yet');
                setFeatureAvailable(false);
            }
            setError(err.message);
            setImpersonationInfo(null);
        } finally {
            setLoading(false);
        }
    };

    // Only check once on mount
    useEffect(() => {
        if (!hasChecked.current) {
            hasChecked.current = true;
            checkImpersonationStatus();
        }
    }, []);

    const impersonateUser = async (userId) => {
        if (!featureAvailable) {
            return { success: false, error: 'Impersonation feature not available on backend yet' };
        }

        try {
            setLoading(true);
            const result = await executeMutation(mutations.adminImpersonateUser, { userId });

            if (result.adminImpersonateUser?.success) {
                // Refresh auth state to get the impersonated user's context
                await refreshAuth();
                // Update impersonation info
                setImpersonationInfo(result.adminImpersonateUser.impersonationInfo);
                return { success: true, message: result.adminImpersonateUser.message };
            } else {
                return { success: false, error: result.adminImpersonateUser?.error || 'Unknown error' };
            }
        } catch (err) {
            console.error('Failed to impersonate user:', err);
            // Check if backend doesn't support this mutation yet
            if (err.message?.includes('Cannot query field') || err.message?.includes('adminImpersonateUser')) {
                setFeatureAvailable(false);
                return { success: false, error: 'Impersonation feature not available on backend yet' };
            }
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const endImpersonation = async () => {
        if (!featureAvailable) {
            return { success: false, error: 'Impersonation feature not available on backend yet' };
        }

        try {
            setLoading(true);
            const result = await executeMutation(mutations.endImpersonation, {});

            if (result.endImpersonation?.success) {
                // Refresh auth state to restore admin context
                await refreshAuth();
                // Clear impersonation info
                setImpersonationInfo(null);
                return { success: true, message: result.endImpersonation.message };
            } else {
                return { success: false, error: result.endImpersonation?.error || 'Unknown error' };
            }
        } catch (err) {
            console.error('Failed to end impersonation:', err);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const isImpersonating = impersonationInfo?.isImpersonation ?? false;

    return (
        <ImpersonationContext.Provider value={{
            impersonationInfo,
            isImpersonating,
            featureAvailable,
            impersonateUser,
            endImpersonation,
            refreshImpersonationInfo: checkImpersonationStatus,
            loading,
            error
        }}>
            {children}
        </ImpersonationContext.Provider>
    );
};
